/**
 * Batch 11 (2026-05-29): shared realtime-status log normalizer.
 *
 * Pre-bump, ~20 hooks/components each declared their own
 * `.subscribe((status, err) => { if (CHANNEL_ERROR || TIMED_OUT || CLOSED)
 * console.error(...) })` block. CLOSED was conflated with the two
 * abnormal states, so the intentional supabase-js cleanup signal that
 * fires on every `removeChannel(...)` (effect teardown, shop switch,
 * date change, route navigation, unmount) flooded the console with red
 * error lines that looked like real failures.
 *
 * Clean rule used by this helper:
 *
 *   - `SUBSCRIBED`            → console.debug (channel up)
 *   - `CLOSED` with no err    → console.debug (normal cleanup)
 *   - `CLOSED` with err set   → console.warn  (abnormal close mid-flight)
 *   - `CHANNEL_ERROR`         → console.error (server-side reject /
 *                                              listener-binding error)
 *   - `TIMED_OUT`             → console.warn  (websocket heartbeat
 *                                              didn't reply; may self-heal)
 *   - any other status        → console.debug (transitional —
 *                                              `joining`, `leaving`, etc.)
 *
 * `source` is the human-readable origin label that prefixes every
 * line. Use a short bracketed name matching the surrounding file so
 * grep / triage stays easy:
 *   `logRealtimeStatus('[useShopReminderCounts]', status, err)`.
 *
 * Returns void; this is purely a normalization shim, not a refetch
 * trigger. Callers that need a self-heal refetch on abnormal close
 * should do that explicitly inside the same status callback, and
 * should NOT trigger it on the normal CLOSED branch (which is the
 * Dashboard pre-bump bug Batch 11 also fixes).
 */
export type RealtimeChannelStatus =
  | 'SUBSCRIBED'
  | 'CLOSED'
  | 'CHANNEL_ERROR'
  | 'TIMED_OUT'
  | string; // supabase-js may emit other transitional values

export function logRealtimeStatus(
  source: string,
  status: RealtimeChannelStatus,
  err?: unknown,
): void {
  if (status === 'CHANNEL_ERROR') {
    console.error(`${source} realtime CHANNEL_ERROR`, err);
    return;
  }
  if (status === 'TIMED_OUT') {
    console.warn(`${source} realtime TIMED_OUT`, err);
    return;
  }
  if (status === 'CLOSED') {
    if (err !== undefined && err !== null) {
      console.warn(`${source} realtime CLOSED with error`, err);
    } else {
      console.debug(`${source} realtime CLOSED (normal cleanup)`);
    }
    return;
  }
  if (status === 'SUBSCRIBED') {
    console.debug(`${source} realtime SUBSCRIBED`);
    return;
  }
  console.debug(`${source} realtime status:`, status, err);
}

/**
 * Convenience predicate for call sites that DO want a refetch / heal
 * action on abnormal closes only. Returns true for CHANNEL_ERROR,
 * TIMED_OUT, and CLOSED-with-err — never for the normal CLOSED-cleanup
 * path that fires on intentional `supabase.removeChannel(...)`.
 *
 * Dashboard.tsx uses this to gate its `ticket-updated` self-heal
 * dispatch so a shop-or-date change doesn't trigger a spurious refetch
 * on the channel that's already being torn down.
 */
export function isAbnormalRealtimeClose(
  status: RealtimeChannelStatus,
  err: unknown,
): boolean {
  if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') return true;
  if (status === 'CLOSED' && err !== undefined && err !== null) return true;
  return false;
}
